import React, { useCallback, useEffect, useRef, useState } from 'react';
import { projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Card, Empty } from '../components/ui';
import {
  Search, Activity, FolderKanban, Pencil, Trash2, Plus,
  Download, Calendar, ChevronDown, ChevronUp, RefreshCw,
  CheckCircle2, XCircle, Filter, Clock,
} from 'lucide-react';

// ─── Config ──────────────────────────────────────────────────────────────────
const ACTION_CONFIG = {
  Create: {
    color: '#06d6a0',
    bg: 'rgba(6,214,160,0.12)',
    border: 'rgba(6,214,160,0.30)',
    icon: Plus,
    label: 'Creates',
  },
  Update: {
    color: '#fbbf24',
    bg: 'rgba(251,191,36,0.12)',
    border: 'rgba(251,191,36,0.30)',
    icon: Pencil,
    label: 'Updates',
  },
  Delete: {
    color: '#f87171',
    bg: 'rgba(248,113,113,0.12)',
    border: 'rgba(248,113,113,0.30)',
    icon: Trash2,
    label: 'Deletes',
  },
};

const FIELD_LABELS = {
  project_name: 'Project Name',
  project_code: 'Code',
  status: 'Status',
  priority: 'Priority',
  start_date: 'Start Date',
  end_date: 'End Date',
  description: 'Description',
  team: 'Team',
  estimated_budget: 'Budget',
  currency: 'Currency',
};

const PAGE_LIMIT = 25;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDateTime = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDateOnly = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

// ─── Sub-components ──────────────────────────────────────────────────────────
function StatCard({ action, count, active, onClick }) {
  const cfg = ACTION_CONFIG[action];
  const Icon = cfg.icon;
  return (
    <div
      onClick={onClick}
      title={`Filter by ${action}`}
      style={{
        padding: '18px 24px',
        borderRadius: 'var(--radius-lg)',
        background: active ? cfg.bg : 'var(--bg-card)',
        border: `1.5px solid ${active ? cfg.color : 'var(--border)'}`,
        cursor: 'pointer',
        transition: 'all 0.22s cubic-bezier(0.34,1.56,0.64,1)',
        display: 'flex', alignItems: 'center', gap: 16,
        transform: active ? 'translateY(-2px)' : 'none',
        boxShadow: active ? `0 8px 24px ${cfg.bg}` : 'none',
        userSelect: 'none',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 14,
        background: cfg.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        flexShrink: 0,
      }}>
        <Icon size={20} />
      </div>
      <div>
        <div style={{
          fontSize: 26, fontWeight: 800, color: active ? cfg.color : 'var(--text)',
          fontFamily: 'var(--font-display)', lineHeight: 1, transition: 'color 0.2s',
        }}>
          {count.toLocaleString()}
        </div>
        <div style={{
          fontSize: 11, color: active ? cfg.color : 'var(--text-muted)',
          fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
          marginTop: 4, transition: 'color 0.2s',
        }}>
          {cfg.label}
        </div>
      </div>
    </div>
  );
}

function ActionBadge({ action }) {
  const cfg = ACTION_CONFIG[action] || { color: 'var(--text)', bg: 'var(--bg-hover)', border: 'var(--border)', icon: Activity, label: action };
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      <Icon size={10} strokeWidth={2.5} />
      {action}
    </span>
  );
}

function AvatarInitial({ name }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: 30, height: 30, borderRadius: '50%',
      background: 'linear-gradient(135deg, var(--accent), #6366f1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0,
      letterSpacing: '0.02em',
    }}>
      {initials}
    </div>
  );
}

function DiffPanel({ changes }) {
  if (!changes || Object.keys(changes).length === 0) return null;
  return (
    <div style={{
      marginTop: 10, padding: '10px 12px',
      borderRadius: 10, background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-dim)', marginBottom: 8 }}>
        Field Changes
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {Object.entries(changes).map(([field, { before, after }]) => (
          <div key={field} style={{
            padding: '5px 10px', borderRadius: 8,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            fontSize: 11, lineHeight: 1.6,
          }}>
            <span style={{ fontWeight: 700, color: 'var(--text-muted)' }}>
              {FIELD_LABELS[field] || field}:{' '}
            </span>
            <span style={{ color: '#f87171', textDecoration: 'line-through', marginRight: 4 }}>
              {String(before ?? '—')}
            </span>
            <span style={{ color: '#06d6a0' }}>
              → {String(after ?? '—')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LogRow({ log, index, expanded, onToggle }) {
  const hasDiff = log.changes && Object.keys(log.changes).length > 0;
  return (
    <>
      <tr
        style={{ borderBottom: '1px solid var(--border-light)', transition: 'background 0.15s', cursor: hasDiff ? 'pointer' : 'default' }}
        onClick={() => hasDiff && onToggle(log.log_id)}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
        onMouseLeave={e => e.currentTarget.style.background = ''}
        title={hasDiff ? 'Click to expand field changes' : undefined}
      >
        {/* # */}
        <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--text-dim)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
          #{index}
        </td>

        {/* Action */}
        <td style={{ padding: '13px 16px' }}>
          <ActionBadge action={log.action} />
        </td>

        {/* Project */}
        <td style={{ padding: '13px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
            <FolderKanban size={13} color="var(--accent)" style={{ flexShrink: 0 }} />
            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', lineHeight: 1.3 }}>
              {log.project_name}
            </span>
          </div>
        </td>

        {/* Code */}
        <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
          {log.project_code
            ? <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: 'var(--bg-hover)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>{log.project_code}</span>
            : <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>—</span>
          }
        </td>

        {/* Performed By */}
        <td style={{ padding: '13px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
            <AvatarInitial name={log.performed_by_name} />
            <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
              {log.performed_by_name || '—'}
            </span>
          </div>
        </td>

        {/* Description */}
        <td style={{ padding: '13px 16px', maxWidth: 300 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {log.description || '—'}
          </div>
          {hasDiff && (
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              {Object.keys(log.changes).length} field{Object.keys(log.changes).length !== 1 ? 's' : ''} changed
            </div>
          )}
        </td>

        {/* Date & Time */}
        <td style={{ padding: '13px 16px', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: 12 }}>
            <Clock size={11} />
            {formatDateTime(log.createdAt)}
          </div>
        </td>
      </tr>

      {/* Expandable diff row */}
      {hasDiff && expanded && (
        <tr style={{ background: 'var(--bg-elevated)' }}>
          <td colSpan={7} style={{ padding: '0 16px 14px 48px' }}>
            <DiffPanel changes={log.changes} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProjectActivityLog() {
  const { user } = useAuth();

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: PAGE_LIMIT });
  const [loading, setLoading] = useState(false);
  const [summaryStats, setSummaryStats] = useState({ Create: 0, Update: 0, Delete: 0 });

  // Filters (local state drives a debounced fetch)
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [page, setPage]         = useState(1);

  const [expandedRows, setExpandedRows] = useState(new Set());
  const debounceRef = useRef(null);

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async (params) => {
    if (user?.role !== 'Admin') return;
    setLoading(true);
    try {
      const res = await projectsAPI.activityLogs({
        ...(params.action   ? { action: params.action }     : {}),
        ...(params.search   ? { search: params.search }     : {}),
        ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
        ...(params.dateTo   ? { dateTo: params.dateTo }     : {}),
        page:  params.page  || 1,
        limit: PAGE_LIMIT,
      });
      setLogs(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, totalPages: 1, total: 0, limit: PAGE_LIMIT });
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Summary stats — always fetch without action filter to get total counts per action
  const fetchStats = useCallback(async () => {
    if (user?.role !== 'Admin') return;
    try {
      const [c, u, d] = await Promise.all([
        projectsAPI.activityLogs({ action: 'Create', limit: 1 }),
        projectsAPI.activityLogs({ action: 'Update', limit: 1 }),
        projectsAPI.activityLogs({ action: 'Delete', limit: 1 }),
      ]);
      setSummaryStats({
        Create: c.data.count || 0,
        Update: u.data.count || 0,
        Delete: d.data.count || 0,
      });
    } catch { /* silent */ }
  }, [user]);

  // Initial load
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Re-fetch when page changes immediately
  useEffect(() => {
    fetchLogs({ action: actionFilter, search, dateFrom, dateTo, page });
  }, [page, actionFilter]); // page & actionFilter are instant

  // Debounce text inputs + date changes
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchLogs({ action: actionFilter, search, dateFrom, dateTo, page: 1 });
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, dateFrom, dateTo]);

  const handleActionFilter = (a) => {
    setActionFilter(a);
    setPage(1);
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleRefresh = () => {
    fetchStats();
    fetchLogs({ action: actionFilter, search, dateFrom, dateTo, page });
  };

  // ── CSV Export ──────────────────────────────────────────────────────────────
  const exportCSV = async () => {
    // Fetch ALL matching records (no page limit) for export
    try {
      const res = await projectsAPI.activityLogs({
        ...(actionFilter ? { action: actionFilter } : {}),
        ...(search       ? { search }               : {}),
        ...(dateFrom     ? { dateFrom }             : {}),
        ...(dateTo       ? { dateTo }               : {}),
        page: 1, limit: 2000,
      });
      const all = res.data.data || [];
      const headers = ['Log ID', 'Project Code', 'Project Name', 'Action', 'Performed By', 'Date & Time', 'Description', 'Changes'];
      const rows = all.map(l => [
        l.log_id,
        l.project_code || '',
        `"${(l.project_name || '').replace(/"/g, '""')}"`,
        l.action,
        `"${(l.performed_by_name || '').replace(/"/g, '""')}"`,
        formatDateTime(l.createdAt),
        `"${(l.description || '').replace(/"/g, '""')}"`,
        l.changes ? `"${JSON.stringify(l.changes).replace(/"/g, '""')}"` : '',
      ]);
      const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `project-activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // ── Access guard ─────────────────────────────────────────────────────────────
  if (user?.role !== 'Admin') {
    return (
      <div className="animate-in" style={{ paddingBottom: 40 }}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 16, padding: '80px 24px', textAlign: 'center',
        }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(248,113,113,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <XCircle size={28} color="#f87171" />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Access Restricted</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', maxWidth: 360 }}>
            The Project Activity Log is only accessible to Administrators.
          </div>
        </div>
      </div>
    );
  }

  const totalEntries = summaryStats.Create + summaryStats.Update + summaryStats.Delete;
  const startIndex = (pagination.page - 1) * pagination.limit + 1;
  const endIndex   = Math.min(pagination.page * pagination.limit, pagination.total);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="animate-in" style={{ paddingBottom: 40 }}>
      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <PageHeader
        title="Project Activity Log"
        subtitle={`${totalEntries.toLocaleString()} total audit entries — full history of all project actions`}
        eyebrow="Audit Trail"
        style={{ '--hero-color': '#7c3aed' }}
        action={
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={handleRefresh}
              disabled={loading}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
                background: 'rgba(255,255,255,0.1)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.25)', borderRadius: 'var(--radius-md)',
                cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
                backdropFilter: 'blur(8px)', transition: 'all 0.2s', opacity: loading ? 0.6 : 1,
              }}
            >
              <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              Refresh
            </button>
            <button
              onClick={exportCSV}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px',
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: 'var(--radius-md)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
                backdropFilter: 'blur(8px)', transition: 'all 0.2s',
              }}
            >
              <Download size={13} /> Export CSV
            </button>
          </div>
        }
      />

      {/* ── Stats Cards ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 16, marginBottom: 24,
      }}>
        {['Create', 'Update', 'Delete'].map(action => (
          <StatCard
            key={action}
            action={action}
            count={summaryStats[action]}
            active={actionFilter === action}
            onClick={() => handleActionFilter(actionFilter === action ? '' : action)}
          />
        ))}
      </div>

      {/* ── Filter Bar ──────────────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 20, padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              id="log-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search project, performer, description…"
              style={{
                width: '100%', padding: '9px 12px 9px 33px', boxSizing: 'border-box',
                borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
                background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13,
                outline: 'none', transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>

          {/* Date From */}
          <div style={{ position: 'relative', flex: '0 1 160px' }}>
            <Calendar size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              id="log-date-from"
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              style={{
                width: '100%', padding: '9px 10px 9px 30px', boxSizing: 'border-box',
                borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
                background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13,
                outline: 'none', colorScheme: 'dark',
              }}
              title="Filter from date"
            />
          </div>

          {/* Date To */}
          <div style={{ position: 'relative', flex: '0 1 160px' }}>
            <Calendar size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input
              id="log-date-to"
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              style={{
                width: '100%', padding: '9px 10px 9px 30px', boxSizing: 'border-box',
                borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
                background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13,
                outline: 'none', colorScheme: 'dark',
              }}
              title="Filter to date"
            />
          </div>

          {/* Action pills */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['', 'Create', 'Update', 'Delete'].map(a => {
              const isAll = a === '';
              const active = actionFilter === a;
              const cfg = isAll ? null : ACTION_CONFIG[a];
              return (
                <button
                  key={a || 'all'}
                  onClick={() => handleActionFilter(a)}
                  style={{
                    padding: '7px 13px', borderRadius: 'var(--radius-md)',
                    fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em',
                    border: `1px solid ${active && cfg ? cfg.color : 'var(--border)'}`,
                    background: active ? (cfg ? cfg.bg : 'var(--accent)') : 'var(--bg-elevated)',
                    color: active ? (cfg ? cfg.color : '#fff') : 'var(--text-muted)',
                    transition: 'all 0.15s',
                  }}
                >
                  {isAll ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Filter size={10} /> All
                    </span>
                  ) : a}
                </button>
              );
            })}
          </div>

          {/* Clear */}
          {(search || actionFilter || dateFrom || dateTo) && (
            <button
              onClick={() => { setSearch(''); setActionFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}
              style={{
                padding: '7px 12px', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)', background: 'transparent',
                color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <XCircle size={11} /> Clear
            </button>
          )}

          {/* Result count */}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {loading ? 'Loading…' : `${pagination.total.toLocaleString()} result${pagination.total !== 1 ? 's' : ''}`}
          </span>
        </div>
      </Card>

      {/* ── Logs Table ──────────────────────────────────────────────────────── */}
      {loading ? (
        <Card style={{ padding: '60px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, border: '3px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading audit log…</div>
        </Card>
      ) : logs.length === 0 ? (
        <Card style={{ padding: '60px 24px' }}>
          <Empty message={
            search || actionFilter || dateFrom || dateTo
              ? 'No entries match your filters. Try adjusting your search or date range.'
              : 'No activity log entries found. Project actions (Create / Update / Delete) will appear here.'
          } />
        </Card>
      ) : (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1.5px solid var(--border)' }}>
                  {['#', 'Action', 'Project', 'Code', 'Performed By', 'Description', 'Date & Time'].map(h => (
                    <th key={h} style={{
                      padding: '11px 16px', textAlign: 'left',
                      fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
                      letterSpacing: '0.07em', color: 'var(--text-muted)', whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <LogRow
                    key={log.log_id}
                    log={log}
                    index={startIndex + i}
                    expanded={expandedRows.has(log.log_id)}
                    onToggle={toggleRow}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Footer: page info + pagination ───────────────────────────────── */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 20px', borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: 12,
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Showing <strong style={{ color: 'var(--text)' }}>{startIndex}–{endIndex}</strong> of{' '}
              <strong style={{ color: 'var(--text)' }}>{pagination.total.toLocaleString()}</strong> entries
            </span>

            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  style={{
                    padding: '5px 13px', borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600,
                    border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text)',
                    cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.4 : 1,
                  }}
                >
                  ← Prev
                </button>

                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - page) <= 1)
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && (
                        <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>…</span>
                      )}
                      <button
                        onClick={() => setPage(p)}
                        style={{
                          minWidth: 34, padding: '5px 10px', borderRadius: 'var(--radius-md)', fontSize: 12,
                          fontWeight: page === p ? 800 : 400,
                          border: `1px solid ${page === p ? 'var(--accent)' : 'var(--border)'}`,
                          background: page === p ? 'var(--accent)' : 'var(--bg-elevated)',
                          color: page === p ? '#fff' : 'var(--text)', cursor: 'pointer',
                        }}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))
                }

                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                  style={{
                    padding: '5px 13px', borderRadius: 'var(--radius-md)', fontSize: 12, fontWeight: 600,
                    border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text)',
                    cursor: page >= pagination.totalPages ? 'not-allowed' : 'pointer',
                    opacity: page >= pagination.totalPages ? 0.4 : 1,
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Spinning keyframe (reuse app global if already defined) */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
